import {
	AppIconRestrictions,
	AvailableIcon,
	getRestrictions,
	SaveAppIconModel,
	createTileIcon,
	updateTileIcon
} from "client/Api/AppIcon/appIcon";
import {
	Dialog,
	DimensionRecommendationKind,
	FileSelector,
	Form,
	FormItem,
	SupportedImageInfo,
	TextInput,
	Toolbar,
	ValidationError
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	useAlerts,
	useDataInputDialog,
	useUnmountedCancellation
} from "client/Hooks";
import {
	getFileMetadata,
	isNotNil
} from "client/Misc/utils";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	IValidationConfiguration
} from "client/Validation";
import {
	inline,
	isEqualTo,
	isNotEmpty,
	isNotNullNorUndefined,
	isSupportedFileType
} from "client/Validation/rules";
import { getDisplayFriendlySize } from "client_legacy/Documents/fileExtensions";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { Base64FileUriUploadWithMetadataModel, fileMetadataSymbol } from "client/Misc/commonTypes";
import { SelectIcon } from "../SelectIcon";
import { AppImageDisplayInfo, RenderImageType } from "client/Api/AppIcon/common";

export type EditAppIconDialogProps = {
	icon?: AvailableIcon;
	onUploaded(icon: AvailableIcon): void;
	onClose: () => void;
};

export const EditAppIconDialog = (props: EditAppIconDialogProps): ReactElement => {
	const {
		icon,
		onUploaded,
		onClose
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [restrictions, setRestrictions] = useState<AppIconRestrictions | null>(null);
	const [formData, setFormData] = useState<FormModel>({
		name: icon?.name ?? "",
		image: { isSet: false }
	});

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const validationConfiguration = useMemo(
		(): IValidationConfiguration<FormModel> => {
			const result: IValidationConfiguration<FormModel> = staticValidationConfiguration.clone();

			if (!icon) {
				result
					.forObject("image")
					.forScalar("isSet")
					.add(
						isEqualTo(true)
					);
			}

			if (!restrictions) {
				return result;
			}

			const {
				supportedImageTypes,
				maxFileSize,
				svgMaxFileSize
			} = restrictions;

			result
				.forObject("image")
				.add(
					isSupportedFileType(supportedImageTypes)
				);

			result
				.forObject("image")
				.whenType(x => x.isSet)
				.forObject("value")
				.add(
					inline((image: Base64FileUriUploadWithMetadataModel) => {
						const {
							size
						} = getFileMetadata(image);

						const isSvg: boolean = image
							.filename
							.toUpperCase()
							.endsWith(".SVG");

						const isWithinSizeLimit = size <= (isSvg ? svgMaxFileSize : maxFileSize);

						if (!isWithinSizeLimit) {
							return {
								kind: InputValidationResultKind.Invalid,
								errorMessage: getImageErrorMessage(maxFileSize, svgMaxFileSize)
							};
						}

						return {
							kind: InputValidationResultKind.Valid
						};
					})
				);

			return result;
		},
		[
			icon,
			restrictions
		]
	);

	const formHandler: IFormHandler<FormModel> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	useEffect(
		() => {
			const loadRestrictions = async (signal: AbortSignal): Promise<void> => {
				try {
					const restrictions: AppIconRestrictions = await getRestrictions(signal);
					setRestrictions(restrictions);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					onClose();
				}
			};

			const cancellation = new AbortController();
			loadRestrictions(cancellation.signal);
			return () => cancellation.abort();
		},
		[alertManager, onClose]
	);

	const fileChangedHandler = (file: File): void => {
		const {
			size,
			name
		} = file;

		if (!restrictions) {
			console.error("Upload restrictions where not loaded yet");
			return;
		}

		const {
			maxFileSize,
			svgMaxFileSize
		} = restrictions;

		const isSvg: boolean = name
			.toUpperCase()
			.endsWith(".SVG");

		const isWithinSizeLimit = size <= (isSvg ? svgMaxFileSize : maxFileSize);

		if (!isWithinSizeLimit) {
			alertManager.showError(getImageErrorMessage(maxFileSize, svgMaxFileSize));
		}
	};

	const uploadHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			const validationResult: InputValidationResult = await formHandler
				.validation
				.validate();

			if (validationResult.kind !== InputValidationResultKind.Valid) {
				return;
			}

			setIsBusy(true);
			try {
				const model: SaveAppIconModel = prepareForSaving(formData);
				let newIcon: AvailableIcon;

				if (icon) {
					newIcon = await updateTileIcon(
						icon.id,
						model,
						globalCancellation
					);
				} else {
					newIcon = await createTileIcon(model, globalCancellation);
				}
				alertManager.showSuccess(localize("_IconHasSuccessfullyBeenUploaded"));
				onUploaded(newIcon);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				if (!formHandler.validation.tryProcessServerError(e)) {
					alertManager.showError(localize("_ChangesNotSaved"));
				}

				setIsBusy(false);
			}
		},
		[
			alertManager,
			formData,
			formHandler.validation,
			globalCancellation,
			icon,
			isBusy,
			onUploaded
		]
	);

	const renderImage = (onClick: () => void): ReactElement => {
		const {
			image
		} = formData;

		let imageInfo: AppImageDisplayInfo | nil = null;

		if (image.isSet) {
			const {
				uri,
				[fileMetadataSymbol]: {
					mimeType
				}
			} = image.value;

			let renderType: RenderImageType;

			switch (mimeType) {
				case "image/png":
					renderType = RenderImageType.Raster;
					break;
				case "image/svg+xml":
					renderType = RenderImageType.Svg;
					break;
				default:
					// We have no known classification, so assume it is the simpliest variant
					renderType = RenderImageType.Raster;
					break;
			}

			imageInfo = {
				imageUrl: uri,
				renderType
			};
		} else if (icon) {
			imageInfo = {
				imageUrl: icon.url,
				renderType: icon.renderType
			};
		}

		return (
			<SelectIcon
				image={imageInfo}
				trackingId="select-icon"
				onClick={onClick}
			/>
		);
	};

	const selectedImage: OptionalModel<Base64FileUriUploadWithMetadataModel> = formData.image;
	const isEdit: boolean = isNotNil(icon);

	return (
		<Dialog
			trackingId="upload-icon-dialog"
			variant="left-slider"
			onClose={onClose}
		>
			<Dialog.Header
				title={isEdit
					? localize("_EditIcon")
					: localize("_NewIcon")
				}
				onClose={onClose}
				onBack={isEdit
					? onClose
					: undefined
				}
				hasBorder
			/>
			<Dialog.Body>
				<Form onSubmit="NONE" hasWidthConstrained>
					<FormItem
						label={localize("_IconName")}
						isRequired
					>
						<TextInput
							{...formHandler.for("name").attachToTextInput()}
						/>
						<ValidationError {...formHandler.for("name").attachToValidationError()}/>
					</FormItem>
					<FormItem
						label={localize("_Icon")}
						isRequired
					>
						<FileSelector
							{...formHandler.for("image").attachToFileSelector()
								.withOnChange(fileChangedHandler)}
						>
							{(onClick) => renderImage(onClick)}
						</FileSelector>
						{restrictions && (
							<SupportedImageInfo
								dimensionRecommendation={{
									kind: DimensionRecommendationKind.Dimension,
									height: 76,
									width: 76
								}}
								svgMaxSize={restrictions.svgMaxFileSize}
								maxFileSize={restrictions.maxFileSize}
								supportedFormats={[
									localize("_FileExtensionSvg"),
									localize("_FileExtensionPng")
								]}
							/>
						)}
						<ValidationError {...formHandler.for("image").attachToValidationError()}/>
					</FormItem>
				</Form>
			</Dialog.Body>
			<Dialog.Footer hasBorder>
				<Toolbar>
					{isEdit
						? (
							<Toolbar.Button
								kind="primary"
								text={localize("_Save")}
								onClick={uploadHandler}
								trackingId="save"
							/>
						)
						: (
							<Toolbar.Button
								kind="primary"
								text={localize("_Upload")}
								isDisabled={!selectedImage.isSet}
								onClick={uploadHandler}
								trackingId="upload"
							/>
						)
					}
					<Toolbar.Button
						kind="secondary"
						text={localize("_Cancel")}
						onClick={onClose}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
EditAppIconDialog.displayName = "EditAppIconDialog";

type FormModel = OverrideProperties<
	SaveAppIconModel,
	{
		image: OptionalModel<Base64FileUriUploadWithMetadataModel>;
	}
>;

const staticValidationConfiguration: IValidationConfiguration<FormModel> = createValidationConfiguration<FormModel>();

staticValidationConfiguration
	.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

function getImageErrorMessage(
	maxFileSize: number,
	svgMaxFileSize: number
): string {
	return localize("_InvalidCustomIconUpload")
		.replace(
			"{maxSize}",
			getDisplayFriendlySize(maxFileSize)
		)
		.replace(
			"{svgMaxSize}",
			getDisplayFriendlySize(svgMaxFileSize)
		);
}

function prepareForSaving(
	formData: FormModel
): SaveAppIconModel {
	const {
		name,
		image
	} = formData;

	return {
		name,
		image
	};
}

type EditAppIconDialogInitialProps = Omit<EditAppIconDialogProps, "onClose" | "onUploaded">;

export function useEditAppIconDialog(): [
	element: ReactElement | null,
	open: (props: EditAppIconDialogInitialProps) => Promise<TryResult<AvailableIcon>>
] {
	return useDataInputDialog(
		(props, onClose, onUploaded) => (
			<EditAppIconDialog
				{...props}
				onClose={onClose}
				onUploaded={onUploaded}
			/>
		)
	);
}